/* ═══════════════════════════════════════════════════
   OFFLINE STORAGE — Capacitor native only
   Handles caching audiobook chapters and ebook content
   to the device's private Filesystem directory.

   ALL functions in this file are gated behind
   Capacitor.isNativePlatform() — they are no-ops
   on the web portal.
═══════════════════════════════════════════════════ */

import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';

// ─── Platform gate ───
export function isNative() {
  return Capacitor.isNativePlatform();
}

// ─── Hash helper: simple SHA-256 hex of uid + contentId ───
async function hashFilename(uid, contentId) {
  const encoder = new TextEncoder();
  const data = encoder.encode(`${uid}_${contentId}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Index management ───
// The index maps contentId -> { path, hash, type, cachedAt }
// Stored in Capacitor Preferences as JSON

const INDEX_KEY = 'offline_content_index';

async function getIndex() {
  const { value } = await Preferences.get({ key: INDEX_KEY });
  return value ? JSON.parse(value) : {};
}

async function setIndex(index) {
  await Preferences.set({ key: INDEX_KEY, value: JSON.stringify(index) });
}

// ═══════════════════════════════════════════════════
// AUDIOBOOK CHAPTER OFFLINE
// ═══════════════════════════════════════════════════

/**
 * Get the offline status of a chapter.
 * Returns 'saved' | 'not_saved'
 * (Downloading state is managed by the component)
 */
export async function getChapterOfflineStatus(uid, chapterId) {
  if (!isNative()) return 'not_saved';
  try {
    const index = await getIndex();
    const key = `audio_${chapterId}`;
    if (index[key]) {
      // Verify file still exists
      try {
        await Filesystem.stat({
          path: index[key].path,
          directory: Directory.Documents,
        });
        return 'saved';
      } catch {
        // File missing — clean up index
        delete index[key];
        await setIndex(index);
        return 'not_saved';
      }
    }
    return 'not_saved';
  } catch {
    return 'not_saved';
  }
}

/**
 * Check offline status for multiple chapters at once.
 * Returns { [chapterId]: 'saved' | 'not_saved' }
 */
export async function getChaptersOfflineStatus(uid, chapterIds) {
  if (!isNative()) return {};
  try {
    const index = await getIndex();
    const result = {};
    for (const chapterId of chapterIds) {
      const key = `audio_${chapterId}`;
      if (index[key]) {
        try {
          await Filesystem.stat({
            path: index[key].path,
            directory: Directory.Documents,
          });
          result[chapterId] = 'saved';
        } catch {
          result[chapterId] = 'not_saved';
        }
      } else {
        result[chapterId] = 'not_saved';
      }
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Save an audio chapter to device storage.
 * Downloads from the signed URL and writes to private Documents dir.
 * Returns the local file URI.
 */
export async function saveChapterOffline(uid, chapterId, audioUrl) {
  if (!isNative()) return null;

  const hash = await hashFilename(uid, chapterId);
  const path = `offline_audio/${hash}.mp3`;

  // Download audio data as base64
  const response = await fetch(audioUrl);
  const blob = await response.blob();
  const base64 = await blobToBase64(blob);

  // Write to private Documents directory (not accessible via Files app / USB)
  await Filesystem.writeFile({
    path,
    data: base64,
    directory: Directory.Documents,
    recursive: true,
  });

  // Update index
  const index = await getIndex();
  index[`audio_${chapterId}`] = {
    path,
    hash,
    type: 'audio',
    cachedAt: new Date().toISOString(),
  };
  await setIndex(index);

  // Return the local file URI for playback
  const fileInfo = await Filesystem.getUri({
    path,
    directory: Directory.Documents,
  });
  return fileInfo.uri;
}

/**
 * Get the local file URI for a cached chapter, or null if not cached.
 */
export async function getCachedChapterUri(uid, chapterId) {
  if (!isNative()) return null;
  try {
    const index = await getIndex();
    const key = `audio_${chapterId}`;
    if (!index[key]) return null;

    const fileInfo = await Filesystem.getUri({
      path: index[key].path,
      directory: Directory.Documents,
    });
    return fileInfo.uri;
  } catch {
    return null;
  }
}

/**
 * Remove a cached chapter from device storage.
 * Only deletes the local file — no effect on Firebase.
 */
export async function removeChapterOffline(uid, chapterId) {
  if (!isNative()) return;
  try {
    const index = await getIndex();
    const key = `audio_${chapterId}`;
    if (index[key]) {
      await Filesystem.deleteFile({
        path: index[key].path,
        directory: Directory.Documents,
      });
      delete index[key];
      await setIndex(index);
    }
  } catch (err) {
    console.warn('[offlineStorage] Failed to remove chapter:', err);
  }
}

// ═══════════════════════════════════════════════════
// EBOOK OFFLINE
// ═══════════════════════════════════════════════════

/**
 * Cache ebook base64 content to device storage.
 * Returns true if this is the first time caching (show toast).
 */
export async function cacheEbookContent(uid, ebookId, base64Content) {
  if (!isNative()) return false;

  const index = await getIndex();
  const key = `ebook_${ebookId}`;
  if (index[key]) {
    // Already cached
    return false;
  }

  const hash = await hashFilename(uid, ebookId);
  const path = `offline_ebooks/${hash}.epub`;

  // Write base64 ePUB to private Documents directory
  await Filesystem.writeFile({
    path,
    data: base64Content,
    directory: Directory.Documents,
    recursive: true,
  });

  // Update index
  index[key] = {
    path,
    hash,
    type: 'ebook',
    cachedAt: new Date().toISOString(),
  };
  await setIndex(index);
  return true; // First cache — show toast
}

/**
 * Get cached ebook base64 content, or null if not cached.
 */
export async function getCachedEbookContent(uid, ebookId) {
  if (!isNative()) return null;
  try {
    const index = await getIndex();
    const key = `ebook_${ebookId}`;
    if (!index[key]) return null;

    const result = await Filesystem.readFile({
      path: index[key].path,
      directory: Directory.Documents,
    });
    return result.data; // base64 string
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════
// OFFLINE PROGRESS SYNC
// ═══════════════════════════════════════════════════

const PENDING_PROGRESS_KEY = 'offline_pending_progress';

/**
 * Save progress locally when offline (Capacitor Preferences).
 * Will be synced to Firestore when connection is restored.
 */
export async function saveProgressOffline(progressData) {
  if (!isNative()) return;
  try {
    const { value } = await Preferences.get({ key: PENDING_PROGRESS_KEY });
    const pending = value ? JSON.parse(value) : [];
    pending.push({ ...progressData, savedAt: new Date().toISOString() });
    await Preferences.set({ key: PENDING_PROGRESS_KEY, value: JSON.stringify(pending) });
  } catch (err) {
    console.warn('[offlineStorage] Failed to save offline progress:', err);
  }
}

/**
 * Get all pending progress entries that need syncing.
 */
export async function getPendingProgress() {
  if (!isNative()) return [];
  try {
    const { value } = await Preferences.get({ key: PENDING_PROGRESS_KEY });
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
}

/**
 * Clear pending progress after successful sync.
 */
export async function clearPendingProgress() {
  if (!isNative()) return;
  await Preferences.remove({ key: PENDING_PROGRESS_KEY });
}

/**
 * Check if the device is currently online.
 */
export async function isOnline() {
  if (!isNative()) return true; // Web is always "online" for our purposes
  try {
    const status = await Network.getStatus();
    return status.connected;
  } catch {
    return true; // Assume online on error
  }
}

/**
 * Register a listener for network status changes.
 * Returns an unsubscribe function.
 */
export function onNetworkChange(callback) {
  if (!isNative()) return () => {};
  const handle = Network.addListener('networkStatusChange', (status) => {
    callback(status.connected);
  });
  return () => handle.remove();
}

// ─── Helpers ───

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
